import React from "react";
import classNames from "classnames";
import { Lazy } from "@/components/Lazy";
import { QRType } from "@/types/qr";
import initPreviewData from "@/data/initial-previews/initPreviewData.json";
import "./InitPreviewSocialLogos.scss";

export interface InitPreviewSocialLogosProps {
  /** Tipo de QR para obtener los logos correspondientes */
  type: QRType;
  /** Color de tema para personalización */
  themeColor?: {
    buttonColor?: string;
    backgroundColor?: string;
  };
  /** Clase CSS adicional */
  className?: string;
}

/**
 * Componente para mostrar logos sociales de placeholder en estado inicial
 * Obtiene los datos de initPreviewData.json y los muestra con lazy loading
 */
export function InitPreviewSocialLogos({
  type,
  themeColor,
  className,
}: InitPreviewSocialLogosProps) {
  // Find initial data for the specific type
  const typeData = React.useMemo(() => {
    return initPreviewData.find((item: { type: string; data?: unknown }) => item.type === type)
      ?.data;
  }, [type]);

  // Apply theme colors as inline styles for customization
  const containerStyle = React.useMemo(() => {
    const style: React.CSSProperties & Record<string, string> = {};

    if (themeColor?.backgroundColor) {
      style.backgroundColor = themeColor.backgroundColor;
    }

    // If buttonColor is provided, we can use it as border or accent color
    if (themeColor?.buttonColor) {
      style.borderColor = themeColor.buttonColor;
      // Use CSS custom properties for child elements to inherit theme
      style["--theme-accent-color"] = themeColor.buttonColor;
    }

    return Object.keys(style).length > 0 ? style : undefined;
  }, [themeColor]);

  const socialLogos = typeData?.socialLogos;

  if (!socialLogos || socialLogos.length === 0) {
    return null;
  }

  const containerClasses = classNames(
    "init-preview-social-logos",
    "init-preview-social-logos__list",
    className
  );

  return (
    <div
      className={containerClasses}
      style={containerStyle}
    >
      {socialLogos.map((logoUrl: string, index: number) => (
        <Lazy
          key={`${logoUrl}-${index}`}
          placeholderClass={`skeleton-placeholder__${type}__logo`}
        >
          <img
            src={logoUrl}
            alt="social media logo"
            className="init-preview-social-logos__item"
          />
        </Lazy>
      ))}
    </div>
  );
}
